/**
 * Users API Service
 * Handles user profile and user management API calls
 */

import apiClient from './axios';
import { ApiResponse, User, PaginatedResponse } from './types';

export const usersApi = {
  /**
   * Get current user's profile
   */
  getProfile: async (): Promise<ApiResponse<{ user: User }>> => {
    const response = await apiClient.get<ApiResponse<{ user: User }>>('/profile');
    return response.data;
  },

  /**
   * Update current user's profile
   */
  updateProfile: async (data: Partial<User>): Promise<ApiResponse<{ user: User }>> => {
    const response = await apiClient.put<ApiResponse<{ user: User }>>('/profile', data);
    return response.data;
  },

  /**
   * Create user (Admin only)
   */
  createUser: async (data: {
    name: string;
    email: string;
    phone?: string;
    phone_country_code?: string;
    password: string;
    password_confirmation: string;
    role?: string;
    region?: string;
  }): Promise<ApiResponse<{ user: User }>> => {
    const response = await apiClient.post<ApiResponse<{ user: User }>>('/users', data);
    return response.data;
  },

  /**
   * Get list of users (Admin/Moderator only)
   */
  getUsers: async (params?: {
    role?: string;
    is_verified?: boolean;
    is_suspended?: boolean;
    region?: string;
    search?: string;
    per_page?: number;
    page?: number;
  }): Promise<PaginatedResponse<User>> => {
    const response = await apiClient.get<PaginatedResponse<User>>('/users', { params });
    return response.data;
  },

  /**
   * Discover users (Public endpoint for all authenticated users)
   * Returns limited public information for user discovery/connection purposes.
   * Supports skills/education filters and weighted matching (match_by_career).
   */
  discoverUsers: async (params?: {
    role?: string;
    region?: string;
    search?: string;
    per_page?: number;
    page?: number;
    skill_ids?: number[];
    education_level_ids?: number[];
    match_by_career?: boolean;
  }): Promise<PaginatedResponse<User>> => {
    const response = await apiClient.get<PaginatedResponse<User>>('/users/discover', { params });
    return response.data;
  },

  /**
   * Get user by UUID-slug (public profile) or ID/username (internal)
   * For public profiles, use /user/{uuid}-{slug} or /user/{username}
   * For internal/admin use, use /users/{id}
   */
  getUser: async (userIdOrUsername: string | number): Promise<ApiResponse<{ user: User }>> => {
    const identifier = String(userIdOrUsername);
    
    // Check if it's a UUID-slug format (contains hyphen and is longer than 36 chars)
    const isUuidSlug = identifier.includes('-') && identifier.length > 36 && 
                       identifier.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i);
    
    // Check if it's a UUID format (exactly 36 chars)
    const isUuid = identifier.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    
    // Check if it's numeric (internal ID)
    const isNumeric = /^\d+$/.test(identifier);
    
    // Use public profile endpoint for UUID-slug, UUID, or username (non-numeric, non-UUID)
    // Use internal endpoint only for numeric IDs
    const endpoint = isNumeric ? `/users/${identifier}` : `/user/${identifier}`;
    const response = await apiClient.get<ApiResponse<{ user: User }>>(endpoint);
    return response.data;
  },

  /**
   * Update user (Admin only)
   */
  updateUser: async (userId: number, data: Partial<User>): Promise<ApiResponse<{ user: User }>> => {
    const response = await apiClient.put<ApiResponse<{ user: User }>>(`/users/${userId}`, data);
    return response.data;
  },

  /**
   * Delete user (Admin only)
   */
  deleteUser: async (userId: number): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/users/${userId}`);
    return response.data;
  },

  /**
   * Verify user (Admin only)
   */
  verifyUser: async (userId: number, notes?: string): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/users/verify', {
      user_id: userId,
      notes,
    });
    return response.data;
  },

  /**
   * Get user statistics (current user)
   */
  getUserStats: async (userId?: number): Promise<ApiResponse<{
    forum_threads: number;
    forum_comments: number;
    blog_posts: number;
    total_posts: number;
    community_contributions: number;
    events_attended: number;
    polls_voted: number;
    connections_count: number;
    participation_streak: number;
  }>> => {
    const endpoint = userId ? `/users/${userId}/stats` : '/users/stats';
    const response = await apiClient.get<ApiResponse<{
      forum_threads: number;
      forum_comments: number;
      blog_posts: number;
      total_posts: number;
      community_contributions: number;
      events_attended: number;
      polls_voted: number;
      connections_count: number;
      participation_streak: number;
    }>>(endpoint);
    return response.data;
  },

  /**
   * Suspend user (Admin only)
   */
  suspendUser: async (userId: number, reason: string): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/users/suspend', {
      user_id: userId,
      suspended: true,
      reason,
    });
    return response.data;
  },

  /**
   * Reset user password (Admin or Super Admin only)
   */
  resetUserPassword: async (
    userId: string | number,
    data: { password: string; password_confirmation: string }
  ): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>(
      `/users/${encodeURIComponent(String(userId))}/reset-password`,
      data
    );
    return response.data;
  },

  /**
   * Unsuspend user (Admin only)
   */
  unsuspendUser: async (userId: number): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/users/suspend', {
      user_id: userId,
      suspended: false,
      reason: null, // Reason not required when unsuspending
    });
    return response.data;
  },

  /**
   * Unverify user (Admin only)
   */
  unverifyUser: async (userId: number): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/users/unverify', {
      user_id: userId,
    });
    return response.data;
  },

  /**
   * Get user activities
   */
  getUserActivities: async (userId?: number, params?: {
    activity_type?: string;
    entity_type?: string;
    start_date?: string;
    end_date?: string;
    per_page?: number;
    page?: number;
  }): Promise<PaginatedResponse<any>> => {
    const endpoint = userId ? `/users/${userId}/activities` : '/profile/activities';
    const response = await apiClient.get<PaginatedResponse<any>>(endpoint, { params });
    return response.data;
  },

  /**
   * Get user online status
   */
  getOnlineStatus: async (userId?: number): Promise<ApiResponse<{
    user_id: number;
    is_online: boolean;
    last_seen: string;
    last_seen_human: string;
  }>> => {
    const endpoint = userId ? `/users/${userId}/online-status` : '/profile/online-status';
    const response = await apiClient.get<ApiResponse<{
      user_id: number;
      is_online: boolean;
      last_seen: string;
      last_seen_human: string;
    }>>(endpoint);
    return response.data;
  },

  /**
   * Update current user's activity (heartbeat)
   */
  updateActivity: async (): Promise<ApiResponse<{
    success: boolean;
    message: string;
    data?: { last_seen: string };
  }>> => {
    const response = await apiClient.post<ApiResponse<{
      success: boolean;
      message: string;
      data?: { last_seen: string };
    }>>('/users/activity');
    return response.data;
  },

  /**
   * Block a user (userId: numeric id or uuid for route).
   */
  blockUser: async (userId: string | number): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>(`/users/${userId}/block`);
    return response.data;
  },

  /**
   * Unblock a user.
   */
  unblockUser: async (userId: string | number): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/users/${userId}/block`);
    return response.data;
  },

  /**
   * List users the current user has blocked.
   */
  getBlockedUsers: async (params?: { page?: number }): Promise<{
    data: Array<{
      id: number;
      uuid: string;
      slug: string;
      name: string;
      username: string | null;
      avatar: string | null;
      blocked_at: string;
    }>;
    meta: { current_page: number; last_page: number; per_page: number; total: number };
  }> => {
    const response = await apiClient.get<{
      data: Array<{
        id: number;
        uuid: string;
        slug: string;
        name: string;
        username: string | null;
        avatar: string | null;
        blocked_at: string;
      }>;
      meta: { current_page: number; last_page: number; per_page: number; total: number };
    }>('/blocked-users', { params });
    return response.data;
  },

  /**
   * Report a user (for moderators to review).
   */
  reportUser: async (
    userId: string | number,
    data: { reason: string; details?: string }
  ): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>(`/users/${userId}/report`, data);
    return response.data;
  },
};

